import React from 'react';
import { Link } from 'react-router-dom';
import { Package, Wrench, MapPin, Sprout, Flower, Snowflake, Check } from 'lucide-react';

export default function About() {
  return (
    <div className="min-h-screen bg-cotton">
      {/* Hero Section */}
      <section className="relative py-20 px-4 md:py-32">
        <div className="container mx-auto max-w-4xl text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-dm-serif text-mulch mb-6">
            Blooma grows with you 🌱
          </h1>
          <p className="text-2xl md:text-3xl font-dm-sans text-mulch mb-4">
            A garden system that works all year
          </p>
          <p className="text-lg md:text-xl font-dm-sans text-mulch opacity-80 mb-8 max-w-2xl mx-auto leading-relaxed">
            Grow indoors now → move outdoors in spring → come back inside when frost hits
            <br />
            <span className="font-medium">Same setup. Same rhythm. No starting over.</span>
          </p>
          <Link
            to="/shop"
            className="inline-block bg-spring-leaf text-white px-8 py-4 rounded-lg hover:bg-heirloom transition-colors font-dm-sans font-medium text-lg shadow-md"
          >
            Explore year-round growing
          </Link>
          <p className="text-sm font-dm-sans text-mulch opacity-60 mt-4">
            No yard required • Small spaces welcome
          </p>
        </div>
      </section>

      {/* What Is Blooma Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-dm-serif text-mulch mb-4">
              What is Blooma?
            </h2>
            <p className="text-lg md:text-xl font-dm-sans text-mulch opacity-80 max-w-3xl mx-auto">
              A growing system — built from products, tools, and guidance that work together.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Card 1: Grow Boxes */}
            <div className="bg-white p-8 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto">
                <Package className="h-8 w-8 text-spring-leaf" />
              </div>
              <h3 className="text-xl font-dm-serif text-mulch mb-3 text-center">
                Grow Boxes
              </h3>
              <p className="text-mulch font-dm-sans text-center leading-relaxed">
                Seasonal, ready-to-use growing setups
              </p>
            </div>

            {/* Card 2: Tools */}
            <div className="bg-white p-8 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto">
                <Wrench className="h-8 w-8 text-spring-leaf" />
              </div>
              <h3 className="text-xl font-dm-serif text-mulch mb-3 text-center">
                Tools
              </h3>
              <p className="text-mulch font-dm-sans text-center leading-relaxed">
                Reusable essentials you use again and again
              </p>
            </div>

            {/* Card 3: Resources */}
            <div className="bg-white p-8 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto">
                <MapPin className="h-8 w-8 text-spring-leaf" />
              </div>
              <h3 className="text-xl font-dm-serif text-mulch mb-3 text-center">
                Resources & guidance
              </h3>
              <p className="text-mulch font-dm-sans text-center leading-relaxed">
                So you always know what to do next
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Seasonal Loop Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-3xl md:text-4xl font-dm-serif text-mulch mb-12 text-center">
            How Blooma works
          </h2>

          <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-4 mb-8">
            {/* Indoor Node */}
            <div className="flex-1 text-center">
              <div className="bg-white p-8 rounded-xl shadow-md">
                <div className="bg-primary-100 w-20 h-20 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <Sprout className="h-10 w-10 text-spring-leaf" />
                </div>
                <h3 className="text-xl font-dm-serif text-mulch mb-2">Indoor</h3>
                <p className="font-dm-sans text-mulch mb-1">Herbs + greens</p>
                <p className="font-dm-sans text-mulch text-sm opacity-70">
                  Guided with Blooma resources
                </p>
              </div>
            </div>

            {/* Arrow */}
            <div className="hidden md:block text-spring-leaf text-4xl">→</div>
            <div className="md:hidden text-spring-leaf text-4xl rotate-90">→</div>

            {/* Outdoor Node */}
            <div className="flex-1 text-center">
              <div className="bg-white p-8 rounded-xl shadow-md">
                <div className="bg-primary-100 w-20 h-20 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <Flower className="h-10 w-10 text-spring-leaf" />
                </div>
                <h3 className="text-xl font-dm-serif text-mulch mb-2">Outdoor</h3>
                <p className="font-dm-sans text-mulch mb-1">Same system</p>
                <p className="font-dm-sans text-mulch text-sm opacity-70">
                  Seasonal Grow Boxes
                </p>
              </div>
            </div>

            {/* Arrow */}
            <div className="hidden md:block text-spring-leaf text-4xl">→</div>
            <div className="md:hidden text-spring-leaf text-4xl rotate-90">→</div>

            {/* Back Indoors Node */}
            <div className="flex-1 text-center">
              <div className="bg-white p-8 rounded-xl shadow-md">
                <div className="bg-primary-100 w-20 h-20 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <Snowflake className="h-10 w-10 text-spring-leaf" />
                </div>
                <h3 className="text-xl font-dm-serif text-mulch mb-2">Back indoors</h3>
                <p className="font-dm-sans text-mulch mb-1">Keep growing</p>
                <p className="font-dm-sans text-mulch text-sm opacity-70">
                  Same tools, new season
                </p>
              </div>
            </div>
          </div>

          <p className="text-center text-2xl font-dm-serif text-mulch">
            Same system → new season
          </p>
        </div>
      </section>

      {/* Why Blooma Exists Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-3xl text-center">
          <p className="text-2xl md:text-3xl font-dm-sans text-mulch leading-relaxed mb-6">
            Most people don't quit because they don't care.
            <br />
            They quit because the tools, timing, and guidance don't line up.
          </p>
          <p className="text-xl md:text-2xl font-dm-sans text-mulch font-medium">
            Blooma brings them together — so growing can stick.
          </p>
        </div>
      </section>

      {/* Start Where You Are Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-dm-serif text-mulch mb-12 text-center">
            Start where you are
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Card 1: Grow Indoors Now */}
            <div className="bg-white p-8 rounded-xl shadow-md hover:shadow-lg transition-all">
              <div className="text-4xl mb-4 text-center">🌿</div>
              <h3 className="text-xl font-dm-serif text-mulch mb-3 text-center">
                Grow indoors now
              </h3>
              <p className="text-mulch font-dm-sans text-center mb-6 leading-relaxed">
                Herbs + greens are the easiest place to begin
              </p>
              <Link
                to="/shop"
                className="block w-full text-center bg-spring-leaf text-white px-6 py-3 rounded-lg hover:bg-heirloom transition-colors font-dm-sans font-medium"
              >
                Shop Herbs & Greens
              </Link>
            </div>

            {/* Card 2: Get Ready for Spring */}
            <div className="bg-white p-8 rounded-xl shadow-md hover:shadow-lg transition-all">
              <div className="text-4xl mb-4 text-center">🌼</div>
              <h3 className="text-xl font-dm-serif text-mulch mb-3 text-center">
                Get ready for spring
              </h3>
              <p className="text-mulch font-dm-sans text-center mb-6 leading-relaxed">
                Make the transition simple
              </p>
              <Link
                to="/shop"
                className="block w-full text-center bg-spring-leaf text-white px-6 py-3 rounded-lg hover:bg-heirloom transition-colors font-dm-sans font-medium"
              >
                See Spring Boxes
              </Link>
            </div>

            {/* Card 3: Not Sure */}
            <div className="bg-white p-8 rounded-xl shadow-md hover:shadow-lg transition-all">
              <div className="text-4xl mb-4 text-center">🤝</div>
              <h3 className="text-xl font-dm-serif text-mulch mb-3 text-center">
                Not sure?
              </h3>
              <p className="text-mulch font-dm-sans text-center mb-6 leading-relaxed">
                We'll guide you
              </p>
              <Link
                to="/shop"
                className="block w-full text-center bg-spring-leaf text-white px-6 py-3 rounded-lg hover:bg-heirloom transition-colors font-dm-sans font-medium"
              >
                Help me choose
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Lite Section */}
      <section className="py-12 px-4">
        <div className="container mx-auto max-w-4xl">
          <h3 className="text-2xl md:text-3xl font-dm-serif text-mulch mb-8 text-center">
            Made for real life
          </h3>

          <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12">
            <div className="flex items-center gap-3">
              <Check className="h-6 w-6 text-spring-leaf flex-shrink-0" />
              <span className="font-dm-sans text-mulch text-lg">Less guessing</span>
            </div>
            <div className="flex items-center gap-3">
              <Check className="h-6 w-6 text-spring-leaf flex-shrink-0" />
              <span className="font-dm-sans text-mulch text-lg">More follow-through</span>
            </div>
            <div className="flex items-center gap-3">
              <Check className="h-6 w-6 text-spring-leaf flex-shrink-0" />
              <span className="font-dm-sans text-mulch text-lg">Fewer abandoned pots</span>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-3xl text-center">
          <p className="text-2xl md:text-3xl font-dm-serif text-mulch mb-3">
            You don't need perfect weather to be a gardener.
          </p>
          <p className="text-xl md:text-2xl font-dm-sans text-mulch mb-8">
            Start small. Keep it going.
          </p>
          <Link
            to="/shop"
            className="inline-block bg-spring-leaf text-white px-8 py-4 rounded-lg hover:bg-heirloom transition-colors font-dm-sans font-medium text-lg shadow-md"
          >
            Explore year-round growing 🌱
          </Link>
        </div>
      </section>
    </div>
  );
}
